import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { ButtonType, SharedConstants, TextType } from '@app/eds';

export type inputSearchUpdateType = {
  input: string;
  isValid: boolean;
};

@Component({
  selector: 'app-input-search',
  templateUrl: './input-search.component.html',
  styleUrls: ['./input-search.component.scss']
})
export class InputSearchComponent implements OnInit {
  // Form Group for the input.
  @Input() inputSearchForm: FormGroup = new FormBuilder().group({
    inputField: ['']
  });

  // Boolean to specify if the input field should be disabled.
  @Input() disableInputField!: boolean;

  // Boolean to specify if the button should be disabled.
  @Input() isButtonDisabled!: boolean;

  // Boolean to specify if the loader should be displayed.
  @Input() isSpinnerVisible = false;

  // Translated error message to show if there is any
  @Input() errorMessage: string = SharedConstants.EMPTY_STRING;

  // Translatable key for the placeholder of the input field.
  @Input() placeholderKey!: string;

  // Translatable key for the label of the input field.
  @Input() inputLabelKey!: string;

  // Translatable key for the  button text.
  @Input() buttonTextKey!: string;

  // Type of the button.
  @Input() buttonType!: ButtonType;

  // Pattern of the input.
  @Input() inputPattern!: string | RegExp;

  // Maximum length of the input.
  @Input() maxInputLength!: number;

  // custom error msg text type.
  @Input() customErrorMsgTextType: TextType = 'body1';

  // If true then a class is applied to make the input field of default color.
  @Input() defaultInputFieldColor = false;

  // Event emitter to emit an event when the input is changed.
  @Output() inputChange = new EventEmitter<inputSearchUpdateType>();

  // Event emitter to emit an event when the close icon is clicked.
  @Output() closeIconClick = new EventEmitter<void>();

  // Event emitter to emit an event when the button is clicked.
  @Output() buttonClick = new EventEmitter<inputSearchUpdateType>();

  // Event emitter to emit an event when enter is clicked.
  @Output() enterClick = new EventEmitter<inputSearchUpdateType>();

  ngOnInit() {
    this.inputPattern &&
      this.getInputSearchFormControl()?.addValidators(
        Validators.pattern(this.inputPattern)
      );

    this.maxInputLength &&
      this.getInputSearchFormControl()?.addValidators(
        Validators.maxLength(this.maxInputLength)
      );
  }

  /**
   * This function gets the data in the input field.
   */
  getInputSearchFormControl = () =>
    this.inputSearchForm?.controls?.['inputField'] as FormControl;

  /**
   * This function is called on the click of the button.
   * Emits an event to indicate the button click.
   */
  onButtonClick = () => {
    const searchValueDetails = this.getSearchedValueDetails();
    this.buttonClick.emit(searchValueDetails);
  };

  /**
   * This function is called when the input changes.
   * Emits the input field text.
   *
   * @param {string} value: The value entered by the user
   */
  onInputChange = (value: string) => {
    const searchValueDetails = this.getSearchedValueDetails(value);
    this.inputChange.emit(searchValueDetails);
  };

  /**
   * This function returns the details of the entered input.
   *
   * @param {string} value: The entered string.
   * @returns the details of the entered string along with its validity.
   */
  getSearchedValueDetails = (value?: string) => {
    const isValid = this.getInputSearchFormControl()?.valid || false;
    const searchedValue = this.getInputSearchFormControl()?.value;
    return { input: value || searchedValue, isValid: isValid };
  };

  /**
   * This function is called on the click of the close icon.
   * Emits an event to indicate the close icon click.
   */
  closeIconClickHandler = () => this.closeIconClick.emit();

  /**
   * This function is called when the enter button is clicked.
   * Emits an event to indicate the enter button click.
   */
  onEnterPress = () => {
    const searchValueDetails = this.getSearchedValueDetails();
    this.enterClick.emit(searchValueDetails);
  };

  /**
   * This function is called to check if the input is invalid.
   *
   * @returns true if the input is invalid, else false
   */
  isInputInvalid = () => !!this.errorMessage || !this.getInputSearchFormControl()?.valid;
}
